import type { WikiTreeNode } from '@akademi/site-builder-runtime';

/**
 * Shared slug helpers for the wiki docs blocks. The wiki detail route is
 * {@code /wiki/:slug*} — the materialized page path IS the slug (it may
 * contain slashes, e.g. {@code guide/setup/install}). {@code usePathname}
 * returns the path without basePath/locale (the proxy rewrites locale
 * internally), so stripping the {@code /wiki/} prefix yields the slug.
 */

/** Current wiki page slug from the router pathname, or {@code ''} on the
 *  landing/search surfaces (no page bound). */
export function currentWikiSlug(pathname: string, base = '/wiki'): string {
  if (!pathname) return '';
  let rest = pathname;
  if (rest.startsWith(base)) rest = rest.slice(base.length);
  rest = rest.replace(/^\/+/, '').replace(/\/+$/, '');
  // Reserved sub-surfaces are not content pages.
  if (rest === '' || rest === 'search' || rest === 'author') return '';
  try {
    return decodeURIComponent(rest);
  } catch {
    return rest;
  }
}

/** DFS the tree into a flat, display-ordered list — the reading order a
 *  prev/next pager walks. */
export function flattenWikiTree(tree: WikiTreeNode[]): WikiTreeNode[] {
  const out: WikiTreeNode[] = [];
  const walk = (nodes: WikiTreeNode[]) => {
    for (const n of nodes) {
      out.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  };
  walk(tree);
  return out;
}

/** Find a node by slug anywhere in the tree. */
export function findWikiNode(tree: WikiTreeNode[], slug: string): WikiTreeNode | null {
  for (const node of tree) {
    if (node.slug === slug) return node;
    const inChild = findWikiNode(node.children, slug);
    if (inChild) return inChild;
  }
  return null;
}

/** URL-safe id from heading text — matches the anchor the TOC links to. */
export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}
